// Book-snapshot presentation logic (TASK-20260728-researcher-observability-ui): the pure half of the Book
// tab. Parses the composer's whole-book snapshots ({ regime, count, holdings: [{symbol, weight, vol,
// exit_score}] }, one per rebalance period) into chartable series. Null-tolerant throughout — a degenerate
// snapshot renders as an empty book, never a crash or a fake holding.

// The validated categorical order (dataviz six-checks pass on the app surface #0a0b0d) — top-N book names
// take these hues IN ORDER; everything beyond folds into the Other gray. Hues follow the ENTITY (a symbol
// keeps its color across snapshots within a mount); never cycled, never generated.
export const BOOK_SERIES_COLORS = {
  series: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'],
  other: '#6b6d70',
};

export function parseBookSeries(rows) {
  return (rows ?? []).map((r) => {
    const snap = r.snapshot ?? {};
    const holdings = Array.isArray(snap.holdings)
      ? snap.holdings
        .filter((h) => h && h.symbol)
        .map((h) => ({
          symbol: String(h.symbol),
          weight: h.weight != null ? Number(h.weight) : null,
          vol: h.vol != null ? Number(h.vol) : null,
          exitScore: h.exit_score != null ? Number(h.exit_score) : null,
        }))
      : [];
    return {
      time: r.barTime,
      regime: snap.regime != null ? Number(snap.regime) : null,
      count: snap.count != null ? Number(snap.count) : holdings.length,
      holdings,
      gross: holdings.reduce((s, h) => s + (h.weight ?? 0), 0),
    };
  }).sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
}

// The stacked weight stream: symbols ranked by PEAK weight across the window; the top N get named series
// (and the fixed hues), the rest fold into `other` — a 26-name book must never invent 26 hues.
export function weightStream(series, topN = 5) {
  if (!series || series.length === 0) return { symbols: [], points: [] };
  const peak = new Map();
  for (const s of series) {
    for (const h of s.holdings) {
      const w = h.weight ?? 0;
      if (w > (peak.get(h.symbol) ?? -Infinity)) peak.set(h.symbol, w);
    }
  }
  const symbols = [...peak.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([sym]) => sym);
  const named = new Set(symbols);
  const points = series.map((s) => {
    const weights = {};
    let other = 0;
    for (const h of s.holdings) {
      const w = h.weight ?? 0;
      if (named.has(h.symbol)) weights[h.symbol] = (weights[h.symbol] ?? 0) + w;
      else other += w;
    }
    return { time: s.time, weights, other };
  });
  return { symbols, points };
}

export function latestBook(series) {
  if (!series || series.length === 0) return null;
  const last = series[series.length - 1];
  return {
    time: last.time,
    regime: last.regime,
    count: last.count,
    gross: last.gross,
    holdings: [...last.holdings].sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0)),
  };
}
